import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Row, Col, Button, Card, Alert, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import BudgetControlTable from '../performance/metrics/performance/BudgetControlTable'; // Reutilizar la tabla
import budgetService from 'services/budgetService';
import paths from 'routes/paths';
import { toast } from 'react-toastify';
import { useAuth } from 'contexts/AuthContext';

const BudgetControlBrandingPage = () => {
  const navigate = useNavigate();
  const [budgetData, setBudgetData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { filterByAssignedClients } = useAuth();

  const loadBudgetData = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      // 🎨 Usar el nuevo endpoint específico de Branding
      const result = await budgetService.getBudgetControlDataBranding(filters);
      
      if (result.success) {
        setBudgetData(result.data || []);
      } else {
        setError('Error al cargar los datos de control de presupuestos Branding');
        toast.error('Error al cargar los datos de Branding');
      }
    } catch (error) {
      console.error('Error loading Branding budget data:', error);
      setError('Error de conexión al cargar los datos de Branding');
      toast.error('Error de conexión al cargar los datos de Branding');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBudgetData();
  }, [loadBudgetData]);

  const budgetControlTableData = useMemo(() => {
    // Filtrar por clientes asignados al usuario
    const filteredData = filterByAssignedClients(budgetData, 'clientId');
    
    return filteredData.map(order => {
      const gastado = parseFloat(order.gastado) || 0;
      const deberiaGastar = parseFloat(order.deberiaGastar) || 0;
      const inversionOffset = parseFloat(order.inversionOffset) || 0;
      const porcentajeOffset = parseFloat(order.porcentajeOffset) || 0;

      // Usar las fechas ya formateadas que vienen del backend
      const fechaDesde = order.fechaDesde || 'N/A';
      const fechaHasta = order.fechaHasta || 'N/A';
      const horaDesde = order.horaDesde || 'N/A';
      const horaHasta = order.horaHasta || 'N/A';

      return {
        id: order.id,
        cliente: order.cliente || 'N/A',
        businessUnit: order.businessUnit || 'N/A',
        descripcion: order.descripcion || 'Sin descripción',
        
        fechaDesde: fechaDesde,
        fechaHasta: fechaHasta,
        horaDesde: horaDesde,
        horaHasta: horaHasta,
        
        moneda: order.moneda || 'USD',
        inversionBruta: parseFloat(order.inversionBruta) || 0,
        inversionNeta: parseFloat(order.inversionNeta) || 0,
        asignado: parseFloat(order.asignado) || 0,
        
        deberiaGastar: deberiaGastar,
        gastado: gastado,
        inversionOffset: inversionOffset,
        porcentajeOffset: porcentajeOffset,
        
        duracionDias: parseInt(order.duracionDias) || 0,
        diasTranscurridos: parseInt(order.diasTranscurridos) || 0,
        diasRestantes: parseInt(order.diasRestantes) || 0,
        progresoTemporal: parseFloat(order.progresoTemporal) || 0,
        velocidadGasto: parseFloat(order.velocidadGasto) || 0,
        
        status: order.status || 'unknown',
        lastCalculated: order.lastCalculated,
        
        clientId: order.clientId,
        dateFrom: order.dateFrom,
        dateTo: order.dateTo,
        
        // 🎨 Indicador de tipo de campaña
        campaignType: 'Branding'
      };
    });
  }, [budgetData, filterByAssignedClients]);

  // Estado especial cuando no hay datos de Branding
  const hasData = budgetControlTableData.length > 0;

  if (loading) {
    return (
      <Container fluid className="px-3">
        <div className="pb-4">
          <div className="mb-4">
            <div className="d-flex align-items-center">
              <FontAwesomeIcon icon="palette" className="me-2 text-success fs-3" />
              <div>
                <h2 className="mb-1">Control de Presupuestos - Branding</h2>
                <h5 className="text-700 fw-semi-bold">
                  Dashboard especializado para campañas de Branding
                </h5>
              </div>
            </div>
          </div>
          
          <Card className="text-center py-5">
            <Card.Body>
              <div className="spinner-border text-success mb-3" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <h5>Cargando datos de Branding...</h5>
              <p className="text-muted">Obteniendo datos del sistema Branding</p>
            </Card.Body>
          </Card>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="px-3">
        <div className="pb-4">
          <div className="mb-4">
            <div className="d-flex align-items-center">
              <FontAwesomeIcon icon="palette" className="me-2 text-success fs-3" />
              <div>
                <h2 className="mb-1">Control de Presupuestos - Branding</h2>
                <h5 className="text-700 fw-semi-bold">
                  Dashboard especializado para campañas de Branding
                </h5>
              </div>
            </div>
          </div>
          
          <Alert variant="danger">
            <Alert.Heading>
              <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
              Error al cargar datos de Branding
            </Alert.Heading>
            <p>{error}</p>
            <hr />
            <div className="d-flex justify-content-end">
              <Button variant="outline-danger" onClick={() => loadBudgetData()}>
                <FontAwesomeIcon icon="redo" className="me-1" />
                Reintentar
              </Button>
            </div>
          </Alert>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="px-3">
      <div className="pb-4">
        <Row className="mb-4">
          <Col xs={12}>
            <div className="p-3 bg-success bg-opacity-10 rounded-3 border border-success border-opacity-25">
              <Row className="align-items-center g-3">
                <Col md={6}>
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon="palette" className="me-3 text-success fs-2" />
                    <div>
                      <h6 className="mb-1 text-800">
                        <Badge bg="success" className="me-2">Branding</Badge>
                        Control de Presupuestos
                      </h6>
                      <p className="text-600 mb-0">
                        Gestiona y controla presupuestos de campañas de Branding
                      </p>
                      <small className="text-muted">
                        {hasData 
                          ? `${budgetControlTableData.length} órdenes Branding encontradas`
                          : 'No hay órdenes de Branding actualmente'
                        }
                      </small>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="d-flex flex-wrap gap-2 justify-content-md-end justify-content-start">
                    <Button 
                      variant="success" 
                      size="sm"
                      onClick={() => navigate(paths.brandingOrderInsert)}
                      className="flex-shrink-0"
                    >
                      <FontAwesomeIcon icon="plus" className="me-1" />
                      Nueva Orden Branding
                    </Button>
                    <Button 
                      variant="outline-success" 
                      size="sm"
                      onClick={() => navigate(paths.brandingOrderInsert)}
                      className="flex-shrink-0"
                    >
                      <FontAwesomeIcon icon="cog" className="me-1" />
                      Gestionar Órdenes
                    </Button>
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => navigate(paths.performanceMetrics)} // Link a la vista de Performance
                      className="flex-shrink-0"
                    >
                      <FontAwesomeIcon icon="bullseye" className="me-1" />
                      Ver Performance
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
        
        {!hasData ? (
          // Mostrar mensaje especial cuando no hay datos de Branding
          <Row>
            <Col xs={12}>
              <Card className="text-center py-5">
                <Card.Body>
                  <FontAwesomeIcon icon="palette" className="fs-1 text-success mb-3 opacity-50" />
                  <h4 className="text-800 mb-3">No hay campañas de Branding activas</h4>
                  <p className="text-600 mb-4">
                    Actualmente no existen órdenes de inserción clasificadas como Branding.<br/>
                    Las nuevas órdenes aparecerán aquí automáticamente cuando se creen con cuentas de tipo Branding.
                  </p>
                  <div className="d-flex gap-2 justify-content-center">
                    <Button 
                      variant="success" 
                      onClick={() => navigate(paths.brandingOrderInsert)}
                    >
                      <FontAwesomeIcon icon="plus" className="me-1" />
                      Crear Primera Orden Branding
                    </Button>
                    <Button 
                      variant="outline-primary" 
                      onClick={() => navigate(paths.performanceBudgetControl)}
                    >
                      <FontAwesomeIcon icon="bullseye" className="me-1" />
                      Ver Órdenes Performance
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        ) : (
          // Mostrar tabla con datos de Branding
          <Row>
            <Col xs={12}>
              <BudgetControlTable 
                data={budgetControlTableData} 
                onApplyFilters={loadBudgetData}
                isLoading={loading}
              />
            </Col>
          </Row>
        )}
      </div>
    </Container>
  );
};

export default BudgetControlBrandingPage; 